/** Growable binary writer and bounds-checked reader. */

export class ByteWriter {
  private buf: Uint8Array;
  private view: DataView;
  pos = 0;
  length = 0;

  constructor(initial = 4096) {
    this.buf = new Uint8Array(initial);
    this.view = new DataView(this.buf.buffer);
  }

  private ensure(n: number): void {
    if (this.pos + n <= this.buf.length) return;
    let size = this.buf.length * 2;
    while (size < this.pos + n) size *= 2;
    const next = new Uint8Array(size);
    next.set(this.buf);
    this.buf = next;
    this.view = new DataView(this.buf.buffer);
  }

  private advance(n: number): void {
    this.pos += n;
    if (this.pos > this.length) this.length = this.pos;
  }

  u8(v: number): this {
    this.ensure(1);
    this.view.setUint8(this.pos, v & 0xff);
    this.advance(1);
    return this;
  }
  i8(v: number): this {
    if (v < -128 || v > 127) throw new RangeError(`i8 out of range: ${v}`);
    return this.u8(v & 0xff);
  }
  bytes(data: ArrayLike<number>): this {
    this.ensure(data.length);
    for (let i = 0; i < data.length; i++) this.buf[this.pos + i] = data[i] & 0xff;
    this.advance(data.length);
    return this;
  }
  fill(v: number, n: number): this {
    this.ensure(n);
    this.buf.fill(v & 0xff, this.pos, this.pos + n);
    this.advance(n);
    return this;
  }
  u16le(v: number): this {
    this.ensure(2);
    this.view.setUint16(this.pos, v & 0xffff, true);
    this.advance(2);
    return this;
  }
  i16le(v: number): this {
    if (v < -32768 || v > 32767) throw new RangeError(`i16 out of range: ${v}`);
    return this.u16le(v & 0xffff);
  }
  u16be(v: number): this {
    this.ensure(2);
    this.view.setUint16(this.pos, v & 0xffff, false);
    this.advance(2);
    return this;
  }
  i16be(v: number): this {
    if (v < -32768 || v > 32767) throw new RangeError(`i16 out of range: ${v}`);
    return this.u16be(v & 0xffff);
  }
  u24le(v: number): this {
    return this.u8(v).u8(v >> 8).u8(v >> 16);
  }
  u24be(v: number): this {
    return this.u8(v >> 16).u8(v >> 8).u8(v);
  }
  u32le(v: number): this {
    this.ensure(4);
    this.view.setUint32(this.pos, v >>> 0, true);
    this.advance(4);
    return this;
  }
  i32le(v: number): this {
    this.ensure(4);
    this.view.setInt32(this.pos, v | 0, true);
    this.advance(4);
    return this;
  }
  u32be(v: number): this {
    this.ensure(4);
    this.view.setUint32(this.pos, v >>> 0, false);
    this.advance(4);
    return this;
  }
  i32be(v: number): this {
    this.ensure(4);
    this.view.setInt32(this.pos, v | 0, false);
    this.advance(4);
    return this;
  }
  f32le(v: number): this {
    this.ensure(4);
    this.view.setFloat32(this.pos, v, true);
    this.advance(4);
    return this;
  }
  /** ASCII text; non-ASCII characters are replaced with '?'. */
  ascii(s: string): this {
    for (const ch of s) this.u8(ch.charCodeAt(0) < 128 ? ch.charCodeAt(0) : 0x3f);
    return this;
  }
  utf16be(s: string): this {
    for (let i = 0; i < s.length; i++) this.u16be(s.charCodeAt(i));
    return this;
  }
  seek(pos: number): this {
    this.pos = pos;
    return this;
  }
  /** Writes at `at` without moving the cursor permanently. */
  patch(at: number, f: (w: this) => void): this {
    const saved = this.pos;
    this.pos = at;
    f(this);
    this.pos = saved;
    return this;
  }
  toBytes(): Uint8Array {
    return this.buf.slice(0, this.length);
  }
}

export class FormatError extends Error {}

export class ByteReader {
  pos = 0;
  private view: DataView;
  constructor(readonly data: Uint8Array) {
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  }
  get remaining(): number {
    return this.data.length - this.pos;
  }
  private need(n: number): void {
    if (this.pos + n > this.data.length) throw new FormatError(t('Unexpected end of file at byte {pos}', { pos: this.pos }));
  }
  u8(): number {
    this.need(1);
    return this.data[this.pos++];
  }
  i8(): number {
    const v = this.u8();
    return v > 127 ? v - 256 : v;
  }
  u16le(): number {
    this.need(2);
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }
  i16le(): number {
    this.need(2);
    const v = this.view.getInt16(this.pos, true);
    this.pos += 2;
    return v;
  }
  u16be(): number {
    this.need(2);
    const v = this.view.getUint16(this.pos, false);
    this.pos += 2;
    return v;
  }
  i16be(): number {
    this.need(2);
    const v = this.view.getInt16(this.pos, false);
    this.pos += 2;
    return v;
  }
  u24le(): number {
    return this.u8() | (this.u8() << 8) | (this.u8() << 16);
  }
  u24be(): number {
    return (this.u8() << 16) | (this.u8() << 8) | this.u8();
  }
  u32le(): number {
    this.need(4);
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }
  i32le(): number {
    this.need(4);
    const v = this.view.getInt32(this.pos, true);
    this.pos += 4;
    return v;
  }
  i32be(): number {
    this.need(4);
    const v = this.view.getInt32(this.pos, false);
    this.pos += 4;
    return v;
  }
  u32be(): number {
    this.need(4);
    const v = this.view.getUint32(this.pos, false);
    this.pos += 4;
    return v;
  }
  f32le(): number {
    this.need(4);
    const v = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return v;
  }
  ascii(n: number): string {
    this.need(n);
    let s = '';
    for (let i = 0; i < n; i++) s += String.fromCharCode(this.data[this.pos + i]);
    this.pos += n;
    return s;
  }
  utf16be(bytes: number): string {
    this.need(bytes);
    let s = '';
    for (let i = 0; i + 1 < bytes; i += 2) s += String.fromCharCode((this.data[this.pos + i] << 8) | this.data[this.pos + i + 1]);
    this.pos += bytes;
    return s;
  }
  skip(n: number): void {
    this.need(n);
    this.pos += n;
  }
  seek(p: number): void {
    if (p < 0 || p > this.data.length) throw new FormatError(t('Invalid position {pos}', { pos: p }));
    this.pos = p;
  }
}import { t } from '../i18n/i18n';

